import { ReactElement, useEffect, useId, useState } from 'react';

import { CloseIcon, HeartIcon } from '@braskai/icons';
import {
  Button,
  Icon,
  IconButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalWrapper,
  Radio,
  Select,
  SelectItem,
  Typography,
} from '@braskai/ui-kit';
import { Controller, Path } from 'react-hook-form';

import { Box, FormControlLabel, SelectChangeEvent, Stack } from '@mui/material';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { ControlledTextField } from '@/shared/ui/form/TextField';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import {
  RECORDING_FEEDBACK_CHAT_OPTIONS,
  RECORDING_FEEDBACK_COMPANY_SIZE_OPTIONS,
  RECORDING_FEEDBACK_ROLE_OPTIONS,
  RECORDING_FEEDBACK_TOOL_OPTIONS,
  RECORDING_FEEDBACK_USE_CASE_OPTIONS,
  RecordingFeedbackBonusForm,
} from '../../lib/validationSchema';
import { useRecordingFeedbackBonusForm } from '../../model/useRecordingFeedbackBonusForm';
import { useRecordingFeedbackBonusModalStore } from '../../model/useRecordingFeedbackBonusModalStore';

type FormName = Path<RecordingFeedbackBonusForm>;
type FormControl = ReturnType<typeof useRecordingFeedbackBonusForm>['control'];
type ClearFormErrors = ReturnType<typeof useRecordingFeedbackBonusForm>['clearErrors'];
type SubmissionResult = NonNullable<ReturnType<typeof useRecordingFeedbackBonusForm>['submissionResult']>;
type SubmissionSuccessViewProps = {
  result: SubmissionResult;
  onClose: () => void;
};

const labelOf = (value: string) => (value === '__other_option__' ? 'Other' : value);

const getSelectValue = (value: unknown, multiple: boolean): unknown => {
  if (multiple) {
    return Array.isArray(value) ? value : [];
  }

  return value ?? '';
};

type ChoiceControlLabelProps = {
  label: string;
  control: ReactElement;
};

const ChoiceControlLabel = ({ label, control }: ChoiceControlLabelProps) => (
  <FormControlLabel
    label={
      <Typography color="grey.700" variant="body-2">
        {label}
      </Typography>
    }
    sx={{
      '& .MuiFormControlLabel-label': {
        minWidth: 0,
      },
      alignItems: 'center',
      gap: 8,
      m: 0,
    }}
    control={control}
  />
);

type RadioGroupFieldProps = {
  name: FormName;
  clearErrors: ClearFormErrors;
  control: FormControl;
  disabled: boolean;
  options: readonly string[];
  question: string;
};

const RadioGroupField = ({ name, clearErrors, control, disabled, options, question }: RadioGroupFieldProps) => {
  const labelId = useId();
  const errorId = useId();

  return (
    <Stack gap={8}>
      <Typography id={labelId} color="grey.700" variant="body-2-md">
        {question}
      </Typography>
      <Controller
        name={name}
        render={({ field, fieldState }) => (
          <Stack
            aria-describedby={fieldState.error ? errorId : undefined}
            aria-labelledby={labelId}
            gap={4}
            role="radiogroup"
          >
            {options.map((option) => (
              <ChoiceControlLabel
                key={option}
                label={labelOf(option)}
                control={
                  <Radio
                    name={field.name}
                    checked={field.value === option}
                    disabled={disabled}
                    value={option}
                    onChange={() => {
                      clearErrors(name);
                      field.onChange(option);
                    }}
                  />
                }
              />
            ))}
            {fieldState.error?.message ? (
              <Typography id={errorId} color="error.700" role="alert" variant="body-2">
                {fieldState.error.message}
              </Typography>
            ) : null}
          </Stack>
        )}
        control={control}
      />
    </Stack>
  );
};

type SelectQuestionFieldProps = {
  name: FormName;
  clearErrors: ClearFormErrors;
  control: FormControl;
  disabled: boolean;
  multiple?: boolean;
  options: readonly string[];
  placeholder: string;
  question: string;
};

const SelectQuestionField = ({
  name,
  clearErrors,
  control,
  disabled,
  multiple = false,
  options,
  placeholder,
  question,
}: SelectQuestionFieldProps) => {
  const labelId = useId();
  const errorId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const selectOptions = options.map((value) => ({ label: labelOf(value), value }));

  useEffect(() => {
    setIsOpen(false);
  }, [name]);

  return (
    <Stack gap={6}>
      <Typography id={labelId} color="grey.700" variant="body-2-md">
        {question}
      </Typography>
      <Controller
        key={name}
        name={name}
        render={({ field, fieldState }) => {
          const value = getSelectValue(field.value, multiple);

          return (
            <>
              <Select
                key={name}
                aria-describedby={fieldState.error ? errorId : undefined}
                disabled={disabled}
                error={!!fieldState.error}
                labelId={labelId}
                multiple={multiple}
                open={isOpen}
                options={selectOptions}
                placeholder={placeholder}
                value={value}
                displayEmpty
                fullWidth
                isMenuInModal
                onChange={(event: SelectChangeEvent<unknown>) => {
                  const nextValue = event.target.value;
                  clearErrors(name);
                  field.onChange(multiple && typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                  setIsOpen(false);
                }}
                onClose={() => setIsOpen(false)}
                onOpen={() => setIsOpen(true)}
              >
                {selectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <Typography color="grey.900" variant="body-1">
                      {option.label}
                    </Typography>
                  </SelectItem>
                ))}
              </Select>
              {fieldState.error?.message ? (
                <Typography id={errorId} color="error.700" role="alert" variant="body-2">
                  {fieldState.error.message}
                </Typography>
              ) : null}
            </>
          );
        }}
        control={control}
      />
    </Stack>
  );
};

const StepDots = ({ activeIndex, total }: { activeIndex: number; total: number }) => (
  <Stack
    aria-current="step"
    aria-label={`Step ${activeIndex + 1} of ${total}`}
    direction="row"
    role="status"
    spacing="12px"
  >
    {Array.from({ length: total }, (_, index) => `step-${index}`).map((id, index) => (
      <Box
        key={id}
        sx={(theme) => ({
          backgroundColor: index === activeIndex ? theme.palette.primary[600] : theme.palette.primary[50],
          borderRadius: '4px',
          height: 8,
          mixBlendMode: index === activeIndex ? 'normal' : 'multiply',
          width: 8,
        })}
      />
    ))}
  </Stack>
);

const getSuccessCopy = (result: SubmissionResult) => {
  if (result === 'calendly') {
    return {
      title: 'Awesome!',
      body: "We'll send a Calendly link within 1–2 days.",
    };
  }

  return {
    title: 'Thanks!',
    body: 'Appreciate you taking the time.',
  };
};

const SubmissionSuccessView = ({ result, onClose }: SubmissionSuccessViewProps) => {
  const { title, body } = getSuccessCopy(result);

  return (
    <ModalContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton aria-label="Close button" disableFixSizeIcon onClick={onClose}>
          <CloseIcon height={24} width={24} />
        </IconButton>
      </Box>

      <Stack
        sx={{
          minHeight: { mobile: 132, tablet: 148 },
          textAlign: 'center',
        }}
        alignItems="center"
        gap={12}
        justifyContent="center"
      >
        <Stack alignItems="center" direction="row" gap={4}>
          <Typography color="grey.900" textAlign="center" variant="title-4-md">
            {title}
          </Typography>
          <Icon color="transparent" component={<HeartIcon />} fill="error.600" size={22} />
        </Stack>

        <Typography color="grey.500" maxWidth={360} textAlign="center" variant="body-1">
          {body}
        </Typography>
      </Stack>
    </ModalContent>
  );
};

const RecordingFeedbackBonusModalComponent = () => {
  const {
    clearErrors,
    control,
    goBack,
    goNext,
    isLastStep,
    isSubmitting,
    stepIndex,
    submissionResult,
    totalSteps,
    watch,
    handleSubmit,
    onClose,
  } = useRecordingFeedbackBonusForm();

  useEffect(() => {
    MixpanelEvents.recordingFeedbackBonusModalShown();
  }, []);

  const role = watch('role');
  const useCase = watch('useCase');
  const tools = watch('tools') ?? [];

  const renderStep = () => {
    switch (stepIndex) {
      case 0: {
        return (
          <Stack gap={16}>
            <SelectQuestionField
              name="role"
              clearErrors={clearErrors}
              control={control}
              disabled={isSubmitting}
              options={RECORDING_FEEDBACK_ROLE_OPTIONS}
              placeholder="Select your role"
              question="What's your role?"
            />
            {role === '__other_option__' ? (
              <ControlledTextField
                label="Tell us your role"
                control={control}
                fieldName="roleOther"
                placeholder="Researcher, Operations, …"
                variant="outlined"
              />
            ) : null}
          </Stack>
        );
      }
      case 1: {
        return (
          <SelectQuestionField
            name="companySize"
            clearErrors={clearErrors}
            control={control}
            disabled={isSubmitting}
            options={RECORDING_FEEDBACK_COMPANY_SIZE_OPTIONS}
            placeholder="Select company size"
            question="How big is your company?"
          />
        );
      }
      case 2: {
        return (
          <ControlledTextField
            label="What's your company website? (optional)"
            control={control}
            fieldName="website"
            placeholder="https://example.com"
            variant="outlined"
            notRequired
          />
        );
      }
      case 3: {
        return (
          <Stack gap={16}>
            <SelectQuestionField
              name="useCase"
              clearErrors={clearErrors}
              control={control}
              disabled={isSubmitting}
              options={RECORDING_FEEDBACK_USE_CASE_OPTIONS}
              placeholder="Select use case"
              question="What did you use the Rask recording tool for?"
            />
            {useCase === '__other_option__' ? (
              <ControlledTextField
                label="Tell us how you used it"
                control={control}
                fieldName="useCaseOther"
                placeholder="How did you use it?"
                variant="outlined"
              />
            ) : null}
          </Stack>
        );
      }
      case 4: {
        return (
          <Stack gap={16}>
            <SelectQuestionField
              name="tools"
              clearErrors={clearErrors}
              control={control}
              disabled={isSubmitting}
              options={RECORDING_FEEDBACK_TOOL_OPTIONS}
              placeholder="Select tools"
              question="What tools do you currently use for screen recording?"
              multiple
            />
            {tools.includes('__other_option__') ? (
              <ControlledTextField
                label="Which tool?"
                control={control}
                fieldName="toolsOther"
                placeholder="Tool name"
                variant="outlined"
              />
            ) : null}
          </Stack>
        );
      }
      case 5: {
        return (
          <RadioGroupField
            name="chatWillingness"
            clearErrors={clearErrors}
            control={control}
            disabled={isSubmitting}
            options={RECORDING_FEEDBACK_CHAT_OPTIONS}
            question="Want to chat with our product team for 30 min?"
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  if (submissionResult) {
    return (
      <Modal size="xs" open onClose={onClose}>
        <SubmissionSuccessView result={submissionResult} onClose={onClose} />
      </Modal>
    );
  }

  return (
    <Modal size="xs" open onClose={isSubmitting ? undefined : onClose}>
      <form onSubmit={handleSubmit}>
        <ModalHeaderDefault
          title="Would you like to get more free minutes?"
          isCloseButtonDisabled={isSubmitting}
          subtitle="Fill out this form, and we will reach out to you."
          onClose={onClose}
        />

        <ModalContent sx={{ pb: 0, px: { mobile: 16, tablet: 24 } }} noPadding>
          {renderStep()}
        </ModalContent>

        <ModalActions>
          <Stack alignItems="center" gap={16} width="100%">
            <Stack direction="row" gap={12} width="100%">
              <Button
                disabled={isSubmitting || stepIndex === 0}
                size="lg"
                variant="outlined"
                fullWidth
                onClick={goBack}
              >
                Back
              </Button>
              {isLastStep ? (
                <Button key="submit" isLoading={isSubmitting} size="lg" type="submit" fullWidth>
                  Continue
                </Button>
              ) : (
                <Button key="continue" disabled={isSubmitting} size="lg" type="button" fullWidth onClick={goNext}>
                  Continue
                </Button>
              )}
            </Stack>
            <StepDots activeIndex={stepIndex} total={totalSteps} />
          </Stack>
        </ModalActions>
      </form>
    </Modal>
  );
};

export const RecordingFeedbackBonusModal = () => (
  <ModalWrapper hook={useRecordingFeedbackBonusModalStore}>
    <RecordingFeedbackBonusModalComponent />
  </ModalWrapper>
);
